import { Divider, Flex, Typography } from 'antd';
import { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { computeEffectiveFilters } from '@components/propertyFilters/computeEffectiveFilter';
import { countActiveFilters } from '@components/propertyFilters/filterUtils';
import { useProject } from '@contexts/Project.context';
import { useAuth } from '@contexts/User.context';
import { useConfirm } from '@contexts/Confirm.context';
import { useCollectionStore } from '@stores/useCollectionStore';
import { PROJECT_MODE } from '@utils/constants';
import { exportCollectionDetections } from '@utils/api';

import useCollectionData from './hooks/useCollectionData';
import useCollectionTree from './hooks/useCollectionTree';
import useCollectionFilters from './hooks/useCollectionFilters';
import useObjectFilters from './hooks/useObjectFilters';
import useCollectionFilterValues from './hooks/useCollectionFilterValues';
import useFilteredFeatures from './hooks/useFilteredFeatures';
import useCollectionNormalizedFeatures from './hooks/useCollectionNormalizedFeatures';
import useDetectionPointer from './hooks/useDetectionPointer';
import { useHexagonConfigHydration } from './hooks/useHexagonConfig';
import useObjectStyle from './hooks/useObjectStyle';
import useCollectionImageMarkers from './hooks/useCollectionImageMarkers';
import useCollectionExportDownload from './hooks/useCollectionExportDownload';
import useCollectionFilterBody from './hooks/useCollectionFilterBody';
import { buildHistogramValues, toSummaryObjects } from './utils/transformUtils';
import CollectionHeader from './components/CollectionHeader';
import CollectionTree from './components/CollectionTree';
import CollectionFilter from './components/CollectionFilter';
import ObjectFilter from './components/ObjectFilter';
import CollectionTooltip from './components/CollectionTooltip';
import CollectionMapLayer from './CollectionMapLayer';
import CollectionBoundaryLayer from './CollectionBoundaryLayer';
import CollectionHexagonLayer from './CollectionHexagonLayer';
import CollectionDspLayer from './CollectionDspLayer';
import CollectionSingleImageLayer from './CollectionSingleImageLayer';
import { LoadingDots } from '@components/loading/LoadingDots';
import NoResult from '../../sidebars/sidebarElements/NoAnalyzesResult';
import MapLoadingIndicator from '../../MapLoadingIndicator';
import { COLLECTION_SIDEBAR_IDS } from './constants';

const { Text } = Typography;

/**
 * Mirrors the LayerSublist pattern used throughout the layers panel.
 * Layer components return <li> and expect to live inside a <ul>.
 */
const LayerSublist = styled.ul`
	margin: 0;
	padding: 0;
	list-style: none;
	display: flex;
	flex-direction: column;
	row-gap: 14px;
`;

/**
 * Finds a node by key in a flat array (supports future nested structures).
 */
const findNode = (nodes, key) => {
	for (const node of nodes) {
		if (node.key === key) return node;
		if (node.children?.length) {
			const found = findNode(node.children, key);
			if (found) return found;
		}
	}
	return null;
};

/**
 * Collection Results section — top-level entry point rendered in the Layers panel.
 *
 * Owns all filter state for this feature:
 *   1. Fetches collection data via useCollectionData (React Query)
 *   2. Initializes tree UI state via useCollectionTree (Zustand)
 *   3. Owns collection-level filter state via useCollectionFilters
 *   4. Owns per-object filter state via useObjectFilters
 *   5. Derives filtered features via useFilteredFeatures and writes to the store
 *   6. Derives per-property histogram values (buildHistogramValues) for the
 *      filter panels' distribution charts
 *   7. Passes hook instances and histogram values down to CollectionFilter and
 *      ObjectFilter as props
 *
 * Keeping both filter hooks here — rather than inside their panel components —
 * means live draft values (currentValues) are available at this level.
 * useFilteredFeatures can then receive them directly as arguments, with no need
 * to route transient UI state through the Zustand store.
 *
 * Data flow:
 *   useCollectionData → data
 *   useCollectionFilters(data) → collectionFilters (live draft + persist)
 *   useObjectFilters(selectedObjectData) → objectFilters (live draft + persist)
 *   useFilteredFeatures(data, collectionFilters, objectFilters)
 *     → store.filteredFeatures (consumed by map layer)
 *   buildHistogramValues(data.features[, objectKey]) → histogramValues
 *     → passed to CollectionFilter/ObjectFilter (consumed by PropertyHistogram)
 */
const CollectionResults = () => {
	const { project, collectionUuid, setActiveSidebar, dispatch, projectMode } = useProject();
	const { tierPremium, isEnterprise, roleAdmin } = useAuth();
	const { confirm } = useConfirm();
	const { uuid: projectUuid } = project ?? {};
	const isSingleImage = projectMode === PROJECT_MODE.SINGLE_IMAGE;
	// Same access gate the model-based DSP layer uses (see LayerView).
	const dspAllowed = tierPremium || isEnterprise || roleAdmin;

	const { data, isLoading, error } = useCollectionData(projectUuid, collectionUuid);
	const selectedObjectId = useCollectionStore(s => s.selectedObjectId);

	// A panel left registered for content that is gone dims the Layers panel behind
	// it (issue #482). Unguarded: closing ids that aren't registered leaves the
	// state untouched, so neither effect below re-renders the context needlessly.
	const closeCollectionPanels = useCallback(() => {
		dispatch({
			type: 'removeActiveSidebar',
			payload: Object.values(COLLECTION_SIDEBAR_IDS),
		});
	}, [dispatch]);

	// Teardown only, for leaving the collection section entirely: the panels are
	// rendered from here, so an unmount drops them from the screen without
	// unregistering them. Not keyed on the collection — a switch is handled
	// centrally by the `setCollectionUuid` reducer case, which resets every open
	// panel. `closeCollectionPanels` is stable, so this never re-runs.
	useEffect(() => closeCollectionPanels, [closeCollectionPanels]);

	// The early returns below unmount the panels outright, running no teardown of
	// their own. `useCloseSidebarWhenEmpty` doesn't reach this — it covers a panel
	// that renders nothing while staying mounted — and the cleanup above misses a
	// refetch of the same collection coming back empty or failing.
	const hasRenderableContent = !isLoading && !!data?.objects?.length;

	useEffect(() => {
		if (!hasRenderableContent) closeCollectionPanels();
	}, [hasRenderableContent, closeCollectionPanels]);

	const selectedObjectData =
		selectedObjectId && data?.objects ? findNode(data.objects, selectedObjectId) : null;

	// Raw per-property values for the mini distribution charts in the filter
	// panels. Collection-level uses every feature; object-level is scoped to
	// the selected object's detections. Recomputed only when the underlying
	// feature set or selected object changes — not on every filter tweak.
	const collectionHistogramValues = useMemo(
		() => buildHistogramValues(data?.features),
		[data?.features]
	);

	const objectHistogramValues = useMemo(() => {
		if (!selectedObjectData) return {};
		return buildHistogramValues(data?.features, selectedObjectData.key);
	}, [data?.features, selectedObjectData]);

	// Everything the collection is configured to detect — including objects with
	// no current detections, which are exactly what a re-run might find. Shown in
	// the boundary editor's "re-run detection?" confirmation.
	const summaryObjects = useMemo(
		() => toSummaryObjects(data?.configDetectionObjects),
		[data?.configDetectionObjects]
	);

	// Initializes expand/check state in the store for the tree
	useCollectionTree(data, collectionUuid);

	// Seeds the hexagon settings from the collection's saved config. Must come
	// after useCollectionTree, whose effect resets the store on a collection
	// switch — effects run in call order, so hydrating second survives it.
	const isHexagonConfigHydrated = useHexagonConfigHydration(collectionUuid, data?.hexagonConfig);

	// Per-object detection pointer — which shape each object draws as on the map.
	// Saves on pick (no Apply), so it owns its own mutation rather than riding
	// along with the object panel's form.
	const {
		pointerByKey,
		hasOverrides,
		setPointer,
		resetPointers: applyResetPointers,
	} = useDetectionPointer(collectionUuid, data?.objects, data?.configDetectionObjects);

	// Reset discards every object's override at once, so confirm first.
	const handleResetPointers = useCallback(async () => {
		const confirmed = await confirm({
			title: 'Reset shapes?',
			message:
				'Every object goes back to the default shape. Your per-object choices for this collection will be discarded.',
			confirmLabel: 'Reset',
			cancelLabel: 'Cancel',
		});
		if (confirmed) applyResetPointers();
	}, [confirm, applyResetPointers]);

	// Collection-level filter state — owned here, passed to CollectionFilter
	const collectionFilters = useCollectionFilters(data, projectUuid, collectionUuid);

	// Per-object filter state — owned here, passed to ObjectFilter.
	// Pass live collection filter values so per-object bounds stay in sync
	// with the collection-level ceiling even before the user hits Apply.
	//
	// Short-circuit when no object panel is open: the live-value remap only
	// matters for the object filter's effective bounds. Without a selected
	// object, returning the raw (stable) collection filters avoids rebuilding
	// this array — and the whole useObjectFilters chain — on every collection
	// slider drag.
	const liveCollectionFilters = useMemo(() => {
		if (!selectedObjectData) return data?.collectionFilters ?? [];
		if (!data?.collectionFilters?.length || !collectionFilters.currentValues) {
			return data?.collectionFilters ?? [];
		}
		return data.collectionFilters.map(f => {
			const live = collectionFilters.currentValues[f.id];
			if (!live) return f;
			return { ...f, selectedMin: live.min ?? f.min, selectedMax: live.max ?? f.max };
		});
	}, [selectedObjectData, data?.collectionFilters, collectionFilters.currentValues]);

	const objectFilters = useObjectFilters(
		selectedObjectData,
		liveCollectionFilters,
		data?.objects,
		collectionUuid
	);

	// Resolves the effective collection- and object-level filter values from
	// both panels' live form state. Computed once here so both the 3857 path
	// (below) and CollectionSingleImageLayer's per-image filtering apply
	// identical filter semantics without duplicating this resolution logic.
	const filterValues = useCollectionFilterValues(
		collectionFilters.currentValues,
		collectionFilters.filterConfigs,
		selectedObjectData?.key ?? null,
		objectFilters.isDirty,
		objectFilters.currentValues,
		objectFilters.effectiveFilterConfigs,
		data?.objects
	);

	// Derives filteredFeatures from full dataset + applied filter state. The set
	// is panel-independent: all saved per-object filters apply at once, with a
	// live override only for the object being actively edited (isDirty).
	useFilteredFeatures(data?.features, filterValues);

	// Each row's active filter count, carried on the node. Counted here because
	// the row can't see the collection-level ceiling — see `countActiveFilters`.
	//
	// Saved values only: the object panel portals over the tree, and closing it
	// forces apply or discard, so an unsaved draft never reaches a visible row.
	const treeNodes = useMemo(
		() =>
			(data?.objects ?? []).map(object => ({
				...object,
				activeFilterCount: countActiveFilters(
					computeEffectiveFilters(object.filters, data?.collectionFilters)
				),
			})),
		[data?.objects, data?.collectionFilters]
	);

	// Normalized-projection features for precise per-image rendering on
	// single-image projects — fails open to [] on error/loading, and the query
	// never fires for orthophoto projects (enabled gate).
	const { features: normalizedFeatures } = useCollectionNormalizedFeatures(
		projectUuid,
		collectionUuid,
		isSingleImage
	);

	// Mark grid features with detection data so image points turn green and the
	// slider can show detection indicators. Uses the same property contract as
	// the model-based flow (feature.get('detections')).
	useCollectionImageMarkers(normalizedFeatures, data?.objects, isSingleImage);

	// Per-object color, edited inline on the tree row. `liveStyling` is non-null
	// only while a picker is open (or its save is in flight); the base style
	// already reflects every object's saved color and opacity.
	const { liveStyling, previewColor, commitColor, cancelColor } = useObjectStyle(
		collectionUuid,
		data?.objects
	);

	const handleFilterClick = useCallback(() => {
		setActiveSidebar({
			sidebarId: COLLECTION_SIDEBAR_IDS.collectionFilter,
			parentId: 'layerView',
		});
	}, [setActiveSidebar]);

	// Server-side detections export. The body is the same per-object map the
	// hexagon and DSP layers post, so the file holds exactly the detections the
	// tree and the map show. Derived here rather than in the header, which stays
	// presentational.
	const { filterBody: detectionsFilterBody, hasVisibleObjects } = useCollectionFilterBody(
		data?.savedCollectionFilters,
		data?.objects
	);

	const { isDownloading, download } = useCollectionExportDownload();

	const handleDownloadDetections = useCallback(
		() =>
			download({
				request: () =>
					exportCollectionDetections(projectUuid, collectionUuid, detectionsFilterBody),
				// Named after the project, plus what was exported: the project alone
				// would collide with the sampling plot export in the same folder.
				title: `${project?.name ?? 'collection'} detections`,
				fileExtension: 'geojson',
				errorMessage: 'Failed to export detections',
				emptyMessage: 'No detections match the current filter',
			}),
		[download, projectUuid, collectionUuid, detectionsFilterBody, project?.name]
	);

	if (isLoading) {
		return (
			<>
				{/* Non-blocking pill over the map, mirroring the model-based
				    DetectionResults loading affordance — the sidebar text below
				    covers the layer-panel area itself. */}
				<MapLoadingIndicator message="Loading detections" hint="this can take a moment" />
				<LoadingDots>Loading</LoadingDots>
			</>
		);
	}

	if (error && !data) {
		return <Text type="danger">Failed to load collection data.</Text>;
	}

	if (!data?.objects?.length) {
		return null;
	}

	const hasDetections = data.totalCount > 0;

	return (
		<>
			{/* Section title — inherits font-size/weight from LayerList (matches "Model") */}
			<div>Collection</div>

			<Flex vertical gap={4} style={{ marginTop: 4 }}>
				{/* Collection name — de-emphasized subtitle below the section title */}
				<Text type="secondary" style={{ fontWeight: 300, fontSize: '0.8rem' }}>
					{data.name}
				</Text>

				{/* Detection Results */}
				<Flex vertical data-tour="collection-objects">
					<CollectionHeader
						onFilterClick={handleFilterClick}
						disabled={!hasDetections}
						onResetPointers={handleResetPointers}
						canResetPointers={hasOverrides}
						onDownload={handleDownloadDetections}
						isDownloading={isDownloading}
						canDownload={hasVisibleObjects}
					/>
					{hasDetections ? (
						<CollectionTree
							nodes={treeNodes}
							onPointerChange={setPointer}
							onColorPreview={previewColor}
							onColorCommit={commitColor}
							onColorCancel={cancelColor}
						/>
					) : (
						<NoResult text="No detections found" />
					)}
				</Flex>
			</Flex>

			<Divider style={{ margin: '8px 0' }} />

			{/* Map layer — <li> components must live inside a <ul> per the layers panel contract */}
			<LayerSublist>
				{hasDetections && (
					<CollectionMapLayer
						objects={data.objects}
						liveStyling={liveStyling}
						pointerByKey={pointerByKey}
					/>
				)}
				<CollectionBoundaryLayer
					collectionUuid={collectionUuid}
					collectionName={data?.name}
					detectionObjects={summaryObjects}
				/>
			</LayerSublist>

			<Divider style={{ margin: '8px 0' }} />

			<LayerSublist>
				{/* Hexagon density overlay — orthophoto only (the endpoint is
				    geographic; single-image has no map-space aggregation). */}
				{/* Gated on hydration, not merely on data: the layer fetches its
				    aggregation on first render, keyed on the store's resolution.
				    Mounting before the saved size lands would aggregate at the
				    default and then immediately re-aggregate at the saved value —
				    two server-side passes on every collection open. */}
				{hasDetections && !isSingleImage && isHexagonConfigHydrated && (
					<CollectionHexagonLayer
						collectionUuid={collectionUuid}
						collectionName={data.name}
						objects={data.objects}
						savedCollectionFilters={data.savedCollectionFilters}
					/>
				)}
				{/* Digital sampling plots — single-image only, and only when the
				    collection itself is single-image: a plot is a drone image
				    footprint, which orthophoto imagery doesn't have (the endpoint
				    rejects either mismatch with 422). Gated to the same tiers as
				    the model-based DSP layer. */}
				{hasDetections &&
					isSingleImage &&
					dspAllowed &&
					data.mode === PROJECT_MODE.SINGLE_IMAGE && (
						<CollectionDspLayer
							collectionUuid={collectionUuid}
							collectionName={data.name}
							objects={data.objects}
							savedCollectionFilters={data.savedCollectionFilters}
						/>
					)}
			</LayerSublist>

			{/* Precise per-image rendering — single-image projects only */}
			{isSingleImage && (
				<CollectionSingleImageLayer
					normalizedFeatures={normalizedFeatures}
					filterValues={filterValues}
					objects={data.objects}
					liveStyling={liveStyling}
				/>
			)}

			<CollectionFilter
				collectionData={data}
				filters={collectionFilters}
				histogramValues={collectionHistogramValues}
			/>
			<ObjectFilter
				objectData={selectedObjectData}
				filters={objectFilters}
				histogramValues={objectHistogramValues}
			/>

			{/* Hover tooltip + click-to-copy-coords on collection detection features.
			    Its own leaf component so hover updates don't re-render this whole tree. */}
			<CollectionTooltip enabled={hasDetections && !isSingleImage} />
		</>
	);
};

export default CollectionResults;
